import Config from '../../config'
import ImageTools from '../../utils/image-tools'
import Ajax from '../../utils/ajax'
import Timer from "../../utils/timer"
import Uploader from '../../utils/uploader'


/**
 * This class represents an image that is stored locally on the users machine,
 * typically selected during an upload process.
 *
 * The class has the same interface as other image classes such as OpeImageAmazon so
 * that images from different sources can be treated the same way by the rest of the app
 * @param mix id Some identification for the image
 * @param {File} file a reference to the local file object that has been selected from the users computer
 * @param {int} width the original width of the image
 * @param {int} height the original height of the image
 */
function OpeImageLocal(id, file, width, height) {
    this.cache = {};
    this.cache.resizedImages = {};
    if (id === null || id === undefined) {
        throw new OpeException("Cannot create new instance of OpeImageLocal because id parameter is null");
    }
    if (file === null || file === undefined) {
        throw new OpeException("Cannot create new instance of OpeImageLocal because file parameter is null");
    }
    if (width === null || width === undefined) {
        throw new OpeException("Cannot create new instance of OpeImageLocal because width parameter is null");
    }
    if (height === null || height === undefined) {
        throw new OpeException("Cannot create new instance of OpeImageLocal because height parameter is null");
    }

    this.id = id;
    this.file = file;
    this.width = width;
    this.height = height;
    this.busyThumbing = false;
    this.visible = false;
    this.serverData = null;
}

/**
 * Get id of image
 * @return {string}
 */
OpeImageLocal.prototype.getId = function () {
    return this.id;
};

/**
 * Get the local file reference
 * @return {File}
 */
OpeImageLocal.prototype.getFile = function () {
    return this.file;
};


/**
 * Visible indicates if this image is currently shown to the customer on the page
 * @param {boolean} visible
 */
OpeImageLocal.prototype.setVisible = function (visible) {
    this.visible = visible;
    if (!this.visible && !this.busyThumbing) {
        this.busyThumbing = false;
    }
};

/**
 * Is this image currently visible on the users screen?
 * @return {Boolean}
 */
OpeImageLocal.prototype.isVisible = function () {
    return this.visible;
};

/**
 * Get sourceId
 * Source id is the id which the image has in it's source (amazon, dropbox, facebook...)
 * @return {string}
 */
OpeImageLocal.prototype.getSourceId = function () {
    return this.file.regId;
};

/**
 * Get server data
 * Server data is data about the image that the server provides after uploading the image.
 * @return {Object}
 */
OpeImageLocal.prototype.getServerData = function () {
    return this.serverData;
};

/**
 * Set server data
 * Server data is data about the image that the server provides after uploading the image.
 * @param {Object} data
 */
OpeImageLocal.prototype.setServerData = function (data) {
    this.serverData = data;
};

/**
 * ASYNC function
 * Typically used when you want to create an html
 * img tag and you want some url or some base64 data
 * to put into the src attribute of the img tag
 * @param {number} maxSize - The maximum height or width of the image in pixels (you might get less than max)
 * @return Promise - promise which then returns base64 encoded data
 */
OpeImageLocal.prototype.getSrc = function (maxSize) {
    var self = this;

    if (this.cache.resizedImages.hasOwnProperty(maxSize)) {
        var image = self.cache.resizedImages[maxSize];
        return Promise.resolve(image.src);
    } else {
        //Load a resized image, return promise
        return new Promise(function (resolve, reject) {
            Uploader.getThumbnail(self.getId(), maxSize)
                .then(function (thumbnail) {
                    var image = new Image();
                    image.crossOrigin = "Anonymous"; //this helps with cross domain access to amazon stored images
                    image.onload = function () {
                        self.cache.resizedImages[maxSize] = image;
                        resolve(image.src);
                    }
                    image.onerror = function () {
                        reject("Failed to load and cache image");
                    }
                    image.src = thumbnail.url;
                })
                .catch(function (err) {
                    logToCentralServer("OpeImageLocal.prototype.getSrc() failed to load resized image", err, 3);
                    throw err;
                });
        });
    }
};

/**
 * ASYNC function
 * This method will give you something to put into the src attribute of an
 * img tag, just like the getSrc method, but with applied filters (rotation, crop, sephia...)
 * If you provide a maxSize param in pixels, it will also resize the image for you.
 * The filter is a json object of the filters to be applied fex:
 * {rotate.angle: 90, rotate.flip: 'FLIP_HORIZONTAL'}
 * @param  {int} maxSize
 * @param {object} filter
 * @return {Promise} which then returns a base64 string of image
 */
OpeImageLocal.prototype.getSrcWithFilter = function (maxSize, filter) {
    var self = this;
    return new Promise(function (resolve, reject) {
        self.getSrc(maxSize).then(function (src) {

            //Check if filters are given
            var hasFilters = false;
            for (var name in filter) {
                if (filter.hasOwnProperty(name)) {
                    hasFilters = true;
                }
            }

            if (hasFilters) {
                var image = self.cache.resizedImages[maxSize];
                if (!image) {
                    logToCentralServer("OpeImageLocal.prototype.getSrcWithFilter() there is no image in resizedImages object", {
                        image: src
                    }, 3);
                    return resolve(src);
                }
                var thumb = ImageTools.getBase64EncodedImage(image);
                var splitSource = thumb.split(',');
                var base64data = splitSource[1];
                var postData = JSON.stringify({data: base64data, operation: filter});

                Ajax.post({
                    url: Config.EditServerUrl + '/api/EditPost/editByData',
                    responseType: 'IMAGE',
                    body: postData,
                    headers: {
                        'Accept': 'image/*',
                        'Content-type': 'application/json'
                    }
                }).then(function (imageUrl) {
                    resolve(imageUrl);
                }).catch(function (error) {
                    logToCentralServer("OpeImageLocal.prototype.getSrcWithFilter() unable to load image", error, 3);
                    reject({
                        message: "Unable to load image with filter data. Please try again later. If the problem persists, please contact customer service and give them the details about the error.",
                        details: "Cannot get image with filter. Url: " + src,
                        error: error,
                        postData: postData
                    });
                });

            } else {
                resolve(src);
            }
        });
    });

};

/**
 * returns Object with data such as originalImageHeight, originalImageWidth
 */
OpeImageLocal.prototype.getImageData = function () {
    return {
        originalImageWidth: this.width,
        originalImageHeight: this.height,
        sourceId: this.getSourceId(),
        dateTaken: this.file.dateTaken
    };
};

/**
 * Get original width of image in pixels
 * @return {int}
 */
OpeImageLocal.prototype.getWidth = function () {
    return this.width;
};

/**
 * Get original height of image in pixels
 * @return {int}
 */
OpeImageLocal.prototype.getHeight = function () {
    return this.height;
};


/**
 * Set the busy thumbing flag so that we don't start several thumbing operations
 * simultaniously. Also delete the flag if it has been more than 20 seconds since
 * it started thumbing (sometimes the operations are not allowed to finish and the flag
 * get stuck)
 * @param {boolean} busy
 */
OpeImageLocal.prototype._setBusyThumbing = function (busy) {
    this.busyThumbing = busy;
};

/**
 * Wait before resizing image.
 * There could be other resize operations ongoing, or the image might not be loaded from local disk yet
 * or there might not be a cached version available. This will check a couple of conditions and then
 * return true when we can proceed to create a thumb
 * @return {Promise}
 */
OpeImageLocal.prototype._waitForImageDataToBeAvailable = function () {
    var self = this;
    return new Promise(function (resolve, reject) {
        function check() {
            if (self.busyThumbing) {
                Timer.set("OpeImageLocal::" + this.id + "waitImageData", function () {
                    check();
                }.bind(self, resolve, reject), 300);
            } else {
                resolve(true);
            }
        }
        check();
    });
};

export default OpeImageLocal;
